// Unified compass and location service using the Capacitor plugin for all platforms
import { logger } from '../../utils/logger.js'
import {
  CairnCompass,
  type CompassReading as PluginCompassReading,
  type LocationReading as PluginLocationReading,
  type ServiceState,
  type ActivityState,
  type ActivityType,
} from 'cairn-compass'

const log = logger.create('compass')

export interface CompassReading {
  /** Compass heading in degrees (0-360, where 0 is North) */
  heading: number
  /** Accuracy of the heading reading in degrees (lower is better) */
  accuracy?: number | undefined
  /** Timestamp of the reading */
  timestamp: number
  /** Source of the reading for debugging */
  source: string
}

export interface LocationReading {
  /** Latitude in decimal degrees */
  latitude: number
  /** Longitude in decimal degrees */
  longitude: number
  /** Altitude in meters (if available) */
  altitude?: number | undefined
  /** Horizontal accuracy in meters */
  accuracy: number
  /** Vertical accuracy in meters (if available) */
  altitudeAccuracy?: number | undefined
  /** GPS bearing in degrees (0-360, North = 0) */
  bearing?: number | undefined
  /** Speed in meters per second */
  speed?: number | undefined
  /** Speed accuracy in meters per second (if available) */
  speedAccuracy?: number | undefined
  /** Timestamp of the reading */
  timestamp: number
  /** Source of location data */
  provider: string
  /** Whether location is from mock provider (debugging) */
  isMock?: boolean | undefined
  /** True if this reading came from the native file buffer (screen-off recovery) */
  buffered?: boolean
  /**
   * Activity classification captured at the moment this GPS fix was recorded.
   * For buffered points this is the activity stamped into the native buffer
   * when the fix was written — not the activity at drain time.
   */
  activity?: ActivityType | undefined
  /** Confidence (0-100) for the per-fix activity classification. */
  activityConfidence?: number | undefined
}

export class CompassService {
  private isTracking = false
  private isLocationTracking = false
  private callbacks: Array<(reading: CompassReading) => void> = []
  private locationCallbacks: Array<(reading: LocationReading) => void> = []
  private serviceStateCallbacks: Array<(state: ServiceState) => void> = []
  private activityCallbacks: Array<(state: ActivityState) => void> = []
  private currentActivity: ActivityState = { activity: 'determining', confidence: 0, timestamp: 0 }
  private permissionGranted = false
  private permissionPromise: Promise<boolean> | null = null

  // Diagnostic log (JS-side events appended to localStorage, read alongside native log)
  private static readonly DIAG_KEY = 'cairn_diagnostic_js'
  private static readonly DIAG_MAX_ENTRIES = 200

  // On-demand compass management
  private compassDemandCount = 0
  private compassInactivityTimer?: number | undefined

  constructor() {
    void this.setupListeners()
  }

  private async setupListeners(): Promise<void> {
    // Listen for compass updates from the unified plugin
    await CairnCompass.addListener('compassUpdate', (data: PluginCompassReading) => {
      const reading: CompassReading = {
        heading: data.heading,
        accuracy: data.accuracy,
        timestamp: data.timestamp,
        source: data.source,
      }
      log.debug(
        `📱 Unified compass: ${data.heading.toFixed(1)}° (accuracy: ${data.accuracy}) from ${data.source}`
      )
      for (const callback of this.callbacks) {
        try {
          callback(reading)
        } catch (err) {
          log.error('Compass callback error:', err)
        }
      }
    })

    // Listen for location updates from the unified plugin
    await CairnCompass.addListener('locationUpdate', (data: PluginLocationReading) => {
      const reading: LocationReading = {
        latitude: data.latitude,
        longitude: data.longitude,
        accuracy: data.accuracy,
        timestamp: data.timestamp,
        provider: data.provider,
        altitude: data.altitude,
        altitudeAccuracy: data.altitudeAccuracy,
        bearing: data.bearing,
        speed: data.speed,
        speedAccuracy: data.speedAccuracy,
        isMock: data.isMock,
        activity: data.activity,
        activityConfidence: data.activityConfidence,
      }
      log.debug(
        `📍 Unified location: ${data.latitude.toFixed(6)}, ${data.longitude.toFixed(6)} (±${data.accuracy}m) from ${data.provider}`
      )
      for (const callback of this.locationCallbacks) {
        try {
          callback(reading)
        } catch (err) {
          log.error('Location callback error:', err)
        }
      }
    })

    // Listen for service state changes
    await CairnCompass.addListener('serviceStateChanged', (data: ServiceState) => {
      log.debug(`🔧 Service state: ${data.service} ${data.isRunning ? 'started' : 'stopped'}`)
      for (const callback of this.serviceStateCallbacks) {
        try {
          callback(data)
        } catch (err) {
          log.error('Service state callback error:', err)
        }
      }
    })

    // Listen for accuracy changes
    await CairnCompass.addListener('accuracyChanged', data => {
      log.debug(
        `📱 Accuracy changed: ${data.accuracy} calibration needed: ${data.needsCalibration}`
      )
    })

    // Listen for activity recognition changes
    await CairnCompass.addListener('activityChanged', (data: ActivityState) => {
      this.currentActivity = data
      log.debug(`🏃 Activity changed: ${data.activity} (${data.confidence}%)`)
      for (const callback of this.activityCallbacks) {
        try {
          callback(data)
        } catch (err) {
          log.error('Activity callback error:', err)
        }
      }
    })
  }

  async startTracking(): Promise<void> {
    if (this.isTracking) {
      return
    }

    try {
      const capabilities = await CairnCompass.getCapabilities()
      log.debug('📱 Compass capabilities:', capabilities)

      // Pure on/off: 50Hz when active (native sensor optimized frequency)
      await CairnCompass.startTracking({
        frequency: 50,
        enableCalibration: true,
      })

      this.isTracking = true
      log.debug('✅ Unified compass tracking started')
    } catch (error) {
      log.error('Failed to start compass tracking:', error)
      throw error
    }
  }

  async stopTracking(): Promise<void> {
    if (!this.isTracking) {
      return
    }

    try {
      await CairnCompass.stopTracking()
      this.isTracking = false
      log.debug('Unified compass tracking stopped')
    } catch (error) {
      log.error('Error stopping compass:', error)
    }
  }

  onHeadingUpdate(callback: (reading: CompassReading) => void): void {
    this.callbacks.push(callback)
  }

  /**
   * Check if location permission is available (waits for permission if not yet determined)
   */
  get isPermissionGranted(): boolean {
    return this.permissionGranted
  }

  async hasLocationPermission(): Promise<boolean> {
    if (this.permissionPromise) {
      return this.permissionPromise
    }

    this.permissionPromise = this.checkLocationPermission()
    return this.permissionPromise
  }

  private async checkLocationPermission(): Promise<boolean> {
    try {
      const result = await CairnCompass.checkLocationPermission()
      this.permissionGranted = result.granted
      this.notifyPermissionChange(result.granted)
      return result.granted
    } catch (error) {
      log.error('Permission check failed:', error)
      this.permissionGranted = false
      this.notifyPermissionChange(false)
      return false
    }
  }

  private notifyPermissionChange(hasPermission: boolean): void {
    // Dispatch event for components to listen to
    if (typeof document !== 'undefined') {
      document.dispatchEvent(
        new CustomEvent('location-permission-changed', {
          detail: { available: hasPermission },
        })
      )
    }
  }

  // Location tracking methods
  async startLocationTracking(options?: {
    interval?: number
    priority?: 'high' | 'balanced' | 'low'
    displacement?: number
    enableForegroundService?: boolean
    /**
     * GNSS HAL batching window in ms. 0 (default) = no batching, fixes
     * delivered immediately. Non-zero lets the GPS chip duty-cycle
     * between wakes — use while the screen is off.
     */
    maxUpdateDelayMillis?: number
  }): Promise<void> {
    if (this.isLocationTracking) {
      return
    }

    try {
      log.debug('🗺️ Starting unified location tracking...')

      // Wait for permission before starting tracking
      const hasPermission = await this.hasLocationPermission()
      if (!hasPermission) {
        throw new Error('Location permission not granted')
      }

      // Now start location tracking - permissions should be granted
      await CairnCompass.startLocationTracking({
        interval: options?.interval ?? 2,
        fastestInterval: 1,
        priority: options?.priority ?? 'high',
        displacement: options?.displacement ?? 1,
        maxUpdateDelayMillis: options?.maxUpdateDelayMillis ?? 0,
        enableForegroundService: options?.enableForegroundService ?? true,
        notification: {
          title: 'Cairn Location Tracking',
          text: 'Recording your location for hiking safety',
          icon: 'ic_notification',
        },
      })

      this.isLocationTracking = true
      log.debug('✅ Unified location tracking started')

      // Request activity recognition permission (non-blocking, optional)
      CairnCompass.requestActivityRecognitionPermission().catch(err => {
        log.debug('Activity recognition permission not available:', err)
      })
    } catch (error) {
      log.warn('Failed to start location tracking:', (error as Error).message ?? error)
      throw error
    }
  }

  async updateLocationParameters(options: {
    interval: number
    priority: 'high' | 'balanced' | 'low'
    displacement: number
    /**
     * GNSS HAL batching window in ms. 0 = no batching (screen on); non-zero
     * lets the GPS chip duty-cycle (screen off while recording).
     */
    maxUpdateDelayMillis?: number
  }): Promise<void> {
    if (!this.isLocationTracking) {
      return
    }

    try {
      await CairnCompass.updateLocationParameters({
        interval: options.interval,
        priority: options.priority,
        displacement: options.displacement,
        maxUpdateDelayMillis: options.maxUpdateDelayMillis ?? 0,
      })
      log.debug(
        `📍 Location parameters updated: interval=${options.interval}s, priority=${options.priority}, displacement=${options.displacement}m, maxDelay=${options.maxUpdateDelayMillis ?? 0}ms`
      )
    } catch (error) {
      log.error('Failed to update location parameters:', error)
    }
  }

  async stopLocationTracking(): Promise<void> {
    if (!this.isLocationTracking) {
      return
    }

    try {
      await CairnCompass.stopLocationTracking()
      this.isLocationTracking = false
      log.debug('Unified location tracking stopped')
    } catch (error) {
      log.error('Error stopping location tracking:', error)
    }
  }

  async getCurrentPosition(): Promise<LocationReading> {
    const data = await CairnCompass.getCurrentPosition()
    return {
      latitude: data.latitude,
      longitude: data.longitude,
      accuracy: data.accuracy,
      timestamp: data.timestamp,
      provider: data.provider,
      altitude: data.altitude,
      altitudeAccuracy: data.altitudeAccuracy,
      bearing: data.bearing,
      speed: data.speed,
      speedAccuracy: data.speedAccuracy,
      isMock: data.isMock,
    }
  }

  /**
   * Flush buffered locations from native that accumulated while the WebView was
   * suspended (screen off). Call this on app resume when location tracking is active.
   */
  async flushBufferedLocations(): Promise<number> {
    if (!this.isLocationTracking) return 0

    try {
      const result = await CairnCompass.getBufferedLocations()
      if (result.count === 0) return 0

      log.debug(`📍 Flushing ${result.count} buffered location updates from native`)
      this.logDiagnostic('BUFFER', `flushing ${result.count} buffered locations`)

      for (const data of result.locations) {
        const reading: LocationReading = {
          latitude: data.latitude,
          longitude: data.longitude,
          accuracy: data.accuracy,
          timestamp: data.timestamp,
          provider: data.provider,
          altitude: data.altitude,
          altitudeAccuracy: data.altitudeAccuracy,
          bearing: data.bearing,
          speed: data.speed,
          speedAccuracy: data.speedAccuracy,
          isMock: data.isMock,
          activity: data.activity,
          activityConfidence: data.activityConfidence,
          buffered: true,
        }
        for (const callback of this.locationCallbacks) {
          try {
            callback(reading)
          } catch (err) {
            log.error('Location callback error during flush:', err)
          }
        }
      }

      return result.count
    } catch (err) {
      log.error('Failed to flush buffered locations:', err)
      return 0
    }
  }

  onLocationUpdate(callback: (reading: LocationReading) => void): void {
    this.locationCallbacks.push(callback)
  }

  onServiceStateChange(callback: (state: ServiceState) => void): void {
    this.serviceStateCallbacks.push(callback)
  }

  onActivityChanged(callback: (state: ActivityState) => void): void {
    this.activityCallbacks.push(callback)
  }

  getCurrentActivity(): ActivityState {
    return this.currentActivity
  }

  // Removed getOptimalCompassFrequency() - using pure on/off (50Hz or 0Hz) instead

  // On-demand compass management
  async requestCompassUpdates(): Promise<void> {
    this.compassDemandCount++
    log.debug(`📱 Compass requested (${this.compassDemandCount} active)`)

    if (!this.isTracking) {
      await this.startTracking()
    }

    // Reset inactivity timer
    if (this.compassInactivityTimer) {
      clearTimeout(this.compassInactivityTimer)
      this.compassInactivityTimer = undefined
    }

    // No auto-stop timer - rely only on visibility management
  }

  async releaseCompassUpdates(): Promise<void> {
    this.compassDemandCount = Math.max(0, this.compassDemandCount - 1)
    log.debug(`📱 Compass released (${this.compassDemandCount} remaining)`)

    if (this.compassDemandCount === 0) {
      log.debug('📱 No compass demand - stopping for battery optimization')
      await this.stopTracking()
    }
  }

  // Check if compass is currently needed
  isCompassInDemand(): boolean {
    return this.compassDemandCount > 0
  }

  /**
   * Append a JS-side diagnostic event. State transitions only — never per-location.
   */
  logDiagnostic(category: string, message: string): void {
    try {
      const timestamp = new Date().toISOString()
      const entry = `${timestamp}\tJS:${category}\t${message}`
      const raw = localStorage.getItem(CompassService.DIAG_KEY)
      const entries: string[] = raw ? JSON.parse(raw) : []
      entries.push(entry)
      // Keep only the most recent entries
      if (entries.length > CompassService.DIAG_MAX_ENTRIES) {
        entries.splice(0, entries.length - CompassService.DIAG_MAX_ENTRIES)
      }
      localStorage.setItem(CompassService.DIAG_KEY, JSON.stringify(entries))
    } catch {
      // localStorage full or unavailable — non-critical
    }
  }

  /**
   * Read the combined diagnostic log: native (file-based) + JS (localStorage).
   * Returns a single string with all entries sorted by timestamp.
   */
  async getDiagnosticLog(): Promise<string> {
    // Native log
    let nativeLog = ''
    try {
      const result = await (
        CairnCompass as unknown as { getDiagnosticLog: () => Promise<{ log: string }> }
      ).getDiagnosticLog()
      nativeLog = result.log || ''
    } catch {
      // Not on native platform
    }

    // JS log
    let jsEntries: string[] = []
    try {
      const raw = localStorage.getItem(CompassService.DIAG_KEY)
      if (raw) {
        jsEntries = JSON.parse(raw)
      }
    } catch {
      // Empty
    }

    // Merge and sort by timestamp
    const allLines = [...nativeLog.split('\n').filter(l => l.trim()), ...jsEntries]
    allLines.sort() // ISO timestamps sort lexicographically
    return allLines.join('\n')
  }

  // Touch handling for compass freezing during map interaction
  async onTouchStart(): Promise<void> {
    // Request compass updates when user interacts with map
    await this.requestCompassUpdates()

    try {
      await CairnCompass.onTouchStart()
    } catch (error) {
      // Silently handle if method not available on web
    }
  }

  async onTouchEnd(): Promise<void> {
    try {
      await CairnCompass.onTouchEnd()
    } catch (error) {
      // Silently handle if method not available on web
    }

    // Don't immediately release - let the timer handle it
    // This prevents rapid start/stop if user touches again soon
  }
}

// Export singleton instance
export const compassService = new CompassService()
export { CompassService as CompassProvider }
